<template>
  <div>
    <div v-if="loading" class="stock_lots_loading">{{ $t("lot_items.loading") }}</div>
    <div v-else-if="!items.length" class="stock_lots_empty">{{ $t("lot_items.available_empty") }}</div>
    <div v-else>
      <div v-for="group in items" :key="group.group_key">
        <div class="info_line" @click="toggleGroup(group.group_key)">
          <div class="info_value">{{ group.lot || "-" }}</div>
          <div class="info_quantity">
            <div class="lot_line_main">{{ group.available_quantity | getFormattedQuantity }} {{ measureUnitSymbol || "" }}</div>
            <div class="lot_line_sub">{{ formatExpirationDate(group.expiration_date) }}</div>
          </div>
          <img
            class="info_image"
            v-show="isExpanded(group.group_key)"
            src="@/assets/images/icons/arrow_up_green.svg"
            @click.stop="toggleGroup(group.group_key)"
          />
          <img
            class="info_image"
            v-show="!isExpanded(group.group_key)"
            src="@/assets/images/icons/arrow_down.svg"
            @click.stop="toggleGroup(group.group_key)"
          />
        </div>
        <div v-if="isExpanded(group.group_key)" class="lot_group_rows">
          <div class="details_btn_container product_warehouse_details" @click="openHistory(group)">
            <img src="@/assets/images/icons/details_file.svg" />
            <span>{{ $t("lot_items.history") }}</span>
          </div>
          <div v-for="row in group.lot_rows" :key="row.uuid" class="info_line lot_group_row">
            <div class="info_value">{{ formatMovementDate(row.movement_date || row.movement_datetime) }}</div>
            <div class="info_quantity">
              <div class="lot_line_main">{{ row.available_quantity | getFormattedQuantity }} {{ measureUnitSymbol || "" }}</div>
            </div>
            <img class="info_image" src="@/assets/images/icons/right_arrow_black.svg" @click.stop="openHistory(row)" />
          </div>
        </div>
      </div>
    </div>
    <LotItemHistory
      :showPopup="historyVisible"
      :lotItem="selectedLotItem"
      :productUuid="productUuid"
      :warehouseUuid="warehouseUuid"
      :productName="productName"
      :warehouseName="warehouseName"
      :departmentName="departmentName"
      :measureUnitSymbol="measureUnitSymbol"
      @hidden="closeHistory"
      @openConsum="openQuickConsumFromHistory"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getFormattedQuantity, mapAvailableLotGroups } from "~/helpers/stockHelper";
import moment from "moment";
import LotItemHistory from "../dialogs/lotItemHistory.vue";

export default {
  name: "StockLotsList",
  props: {
    productUuid: {
      type: String,
      default: "",
    },
    warehouseUuid: {
      type: String,
      default: "",
    },
    warehouseName: {
      type: String,
      default: "",
    },
    productName: {
      type: String,
      default: "",
    },
    departmentName: {
      type: String,
      default: "",
    },
    active: {
      type: Boolean,
      default: false,
    },
    measureUnitSymbol: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      historyVisible: false,
      selectedLotItem: null,
      expandedKeys: [],
    };
  },
  components: {
    LotItemHistory,
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
  },
  watch: {
    active(newVal) {
      if (newVal) {
        this.fetchLots();
      }
    },
    productUuid() {
      if (this.active) {
        this.fetchLots();
      }
    },
    warehouseUuid() {
      if (this.active) {
        this.fetchLots();
      }
    },
  },
  methods: {
    ...mapActions({
      getSmStockMovementItemsAvailableSum: "smStocks/getSmStockMovementItemsAvailableSum",
    }),
    async fetchLots() {
      if (!this.active || !this.productUuid || !this.warehouseUuid) {
        this.items = [];
        return;
      }

      this.loading = true;
      try {
        const payload = {
          product_uuid: this.productUuid,
          warehouse_uuid: this.warehouseUuid,
          enablePagination: 0,
          limit: 99999,
        };
        const response = await this.getSmStockMovementItemsAvailableSum(payload);
        const data = response?.data?.data || response?.data || [];
        this.items = mapAvailableLotGroups(data);
        this.expandedKeys = [];
      } finally {
        this.loading = false;
      }
    },
    toggleGroup(groupKey) {
      const idx = this.expandedKeys.indexOf(groupKey);
      if (idx === -1) {
        this.expandedKeys.push(groupKey);
      } else {
        this.expandedKeys.splice(idx, 1);
      }
    },
    isExpanded(groupKey) {
      return this.expandedKeys.includes(groupKey);
    },
    formatMovementDate(value) {
      if (!value) {
        return "-";
      }
      const date = moment(value);
      if (!date.isValid()) {
        return value;
      }
      return date.format(this.getDefaultDateFormat);
    },
    formatQty(value) {
      return getFormattedQuantity(value || 0);
    },
    formatExpirationDate(value) {
      if (!value) {
        return "-";
      }
      const date = moment(value);
      if (!date.isValid()) {
        return value;
      }
      return date.format(this.getDefaultDateFormat);
    },
    openHistory(item) {
      this.selectedLotItem = item;
      this.historyVisible = true;
    },
    closeHistory() {
      this.historyVisible = false;
    },
    openQuickConsumFromHistory() {
      this.historyVisible = false;
      this.$emit("openQuickConsum");
    },
  },
  mounted() {},
};
</script>

<style scoped>
.lot_line_main {
  font-weight: 600;
  text-align: right;
}

.lot_line_sub {
  font-size: 12px;
  color: #6b6b6b;
  text-align: right;
}

.stock_lots_loading,
.stock_lots_empty {
  font-size: 12px;
  color: #6b6b6b;
  padding: 4px 0 8px;
}

.lot_group_rows {
  padding-left: 16px;
}

.lot_group_row {
  border-top: 1px dashed #e4e4e4;
}

.product_warehouse_details {
  height: 40px;
}
</style>
